import React, { useCallback, useEffect, useState } from 'react'

import { Button, Col, Form, Input, Row } from 'antd'
import { FormattedMessage, useIntl } from 'react-intl'

import { LimitedColourPicker } from 'components/ColorPicker/LimitedColourPicker'
import { Select } from 'components/Dropdown/Select'
import { Instructions } from 'components/Map/Instructions'
import { RangePicker } from 'components/RangePicker/RangePicker'
import { useForecasters } from 'hooks/forecasters'
import { useHighwayForecasts } from 'hooks/highwayForecasts'
import { postHighwayForecast } from 'services/HighwayForecasts'
import messages from 'services/intl/messageDefinitions'
import { HighwayForecastStore } from 'stores/HighwayForecastStore'
import { useId, useLocale } from 'stores/UserStore'
import { createEmptyHighwayForecast } from 'utils/Highways'
import { useSendNotification } from 'utils/Notifications'
import { COLOUR_MAP, COLOUR_NAMES } from 'utils/Product'

export const CreateForm = ({
    toggleEdit,
    update,
    initialValues = createEmptyHighwayForecast(),
    editing,
    closeDrawer,
    autoUpdate = false,
}) => {
    const intl = useIntl()
    const [form] = Form.useForm()

    const [disabledForm, toggleDisabledForm] = useState(editing && !initialValues.userID)
    const [name, setName] = useState(initialValues.name)
    const [nameTouched, setNameTouched] = useState(false)
    const [dateRange, setDateRange] = useState([initialValues.issueDateTime, initialValues.expiryDateTime])
    const userId = useId()
    const draftHighwayForecast = HighwayForecastStore.useDraftProduct()
    const updateDraftHighwayForecast = HighwayForecastStore.useUpdateDraftProduct()
    const resetDraft = HighwayForecastStore.useResetDraftProduct()
    const locale = useLocale()
    const sendNotification = useSendNotification()
    const { mutate } = useHighwayForecasts()
    const { forecasters } = useForecasters()

    // Set the user ID to the current user if it is not set and we're not editing an existing forecast (ie a cloned forecast)
    const formattedUserId = initialValues.userID ? initialValues.userID : editing ? null : userId
    const formattedInitialValues = {
        ...initialValues,
        name: initialValues.name,
        userID: formattedUserId,
        times: [new Date(initialValues.issueDateTime), new Date(initialValues.expiryDateTime)],
    }
    const [colour, setColour] = useState(editing ? formattedInitialValues.colour : draftHighwayForecast.colour)

    const createNewHighwayForecast = (formData, errors) => {
        const newSetup = createEmptyHighwayForecast(locale)
        newSetup.colour = draftHighwayForecast.colour // TODO have only a single source of truth
        newSetup.status = 'draft'
        newSetup.name = formData.name
        newSetup.userID = formData.userID
        newSetup.expiryDateTime = dateRange[1]
        newSetup.issueDateTime = dateRange[0]
        newSetup.polygons = draftHighwayForecast.polygons // TODO have only a single source of truth

        apiPost(newSetup, locale)
    }

    const apiPost = useCallback(async (data, locale) => {
        await postHighwayForecast(data, locale)
        mutate()
        resetDraft()
        sendNotification(intl.formatMessage({ ...messages.highwayForecastSaved }, { name: data.name }))
        closeDrawer()
        form.resetFields()
    }, [])

    const cancelDraft = () => {
        closeDrawer()
        form.resetFields()
    }

    const updateDetails = useCallback(
        changedField => {
            const updateData = {
                key: null,
                value: null,
            }

            switch (changedField.key) {
                case 'name':
                    // wait a couple seconds to let user finish typing
                    const formattedName = changedField.value.trim() // remove trailing or procedding white space
                    if (formattedName.length >= 3 && formattedName.length <= 100) {
                        setName(formattedName)
                        setNameTouched(true)
                    }
                    break
                case 'times':
                    break
                default:
                    updateData.value = changedField.value
                    updateData.key = changedField.key
            }

            if (updateData.value && updateData.key) {
                update([updateData])
            }
        },
        [update]
    )

    const updateDates = dates => {
        setDateRange(dates)
        update([
            {
                key: 'expiryDateTime',
                value: dates[1],
            },
            {
                key: 'issueDateTime',
                value: dates[0],
            },
        ])
    }

    const changeColour = useCallback(
        colour => {
            setColour(colour)
            if (editing && autoUpdate) {
                const updateData = {
                    key: 'colour',
                    value: colour,
                }
                updateDetails(updateData)
            } else {
                updateDraftHighwayForecast({
                    ...draftHighwayForecast,
                    colour,
                })
            }
        },
        [autoUpdate, draftHighwayForecast, editing, updateDetails, updateDraftHighwayForecast]
    )

    const autoChecker = changeValues => {
        const changedField = Object.keys(changeValues)[0]
        const parsedValue = Object.values(changeValues)[0]
        const updateData = { key: changedField, value: parsedValue }

        if (changedField === 'userID') {
            // now that there is a forecaster allow for editing other fields
            toggleDisabledForm(false)
        }

        if (autoUpdate) {
            updateDetails(updateData)
        }
    }

    // prevent multiple PUT request when user is typing
    useEffect(() => {
        const handler = setTimeout(() => {
            if (autoUpdate && nameTouched) {
                const updateData = {
                    key: 'name',
                    value: name,
                }
                update([updateData])
                setNameTouched(false)
            }
        }, 1500)

        return () => {
            clearTimeout(handler)
        }
    }, [autoUpdate, name, nameTouched, update])

    return (
        <div>
            {editing ? (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.editHighwayForecast} />
                </h1>
            ) : (
                <h1 style={styles.title}>
                    <FormattedMessage {...messages.createHighwayForecast} />
                </h1>
            )}
            <Instructions />
            <Form
                form={form}
                layout="horizontal"
                onFinish={createNewHighwayForecast}
                autoComplete="on"
                onValuesChange={autoChecker}
                initialValues={formattedInitialValues}
            >
                <Form.Item
                    label={<FormattedMessage {...messages.highwayForecastName} />}
                    labelCol={{ span: 24 }}
                    name="name"
                    rules={[
                        { required: true, message: <FormattedMessage {...messages.nameRequired} /> },
                        { min: 3, message: <FormattedMessage {...messages.nameTooShort} /> },
                        { max: 100, message: <FormattedMessage {...messages.nameTooLong} /> },
                    ]}
                >
                    <Input disabled={disabledForm} />
                </Form.Item>
                <Row gutter={16}>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.forecaster} />}
                            labelCol={{ span: 24 }}
                            name="userID"
                            rules={[{ required: true }]}
                        >
                            <Select data-test={'forecasterSelect'}>
                                {forecasters?.map((item, i) => {
                                    return (
                                        <Select.Option key={i} value={item.id}>
                                            {item.name}
                                        </Select.Option>
                                    )
                                })}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col className="gutter-row" span={12}>
                        <Form.Item
                            label={<FormattedMessage {...messages.colour} />}
                            labelCol={{ span: 24 }}
                            name="colour"
                            rules={[{ required: false }]}
                        >
                            <LimitedColourPicker
                                colourMap={COLOUR_MAP}
                                colourNames={COLOUR_NAMES}
                                disabled={disabledForm}
                                initialColour={colour}
                                onUpdate={changeColour}
                            />
                        </Form.Item>
                    </Col>
                </Row>
                <Form.Item
                    label={<FormattedMessage {...messages.issueAndExpiry} />}
                    labelCol={{ span: 24 }}
                    name="times"
                    rules={[{ required: true, message: <FormattedMessage {...messages.forecastValidationDate} /> }]}
                >
                    <RangePicker
                        initValues={formattedInitialValues.times}
                        onChange={updateDates}
                        disabled={disabledForm}
                    />
                </Form.Item>
            </Form>
            {editing ? (
                <div style={styles.buttonContainer}>
                    <Button type="primary" onClick={toggleEdit} data-test={'closeEditingButton'}>
                        <FormattedMessage {...messages.closeEditing} />
                    </Button>
                </div>
            ) : (
                <div style={styles.buttonContainer}>
                    <Button onClick={cancelDraft} style={styles.cancelButton}>
                        <FormattedMessage {...messages.cancel} />
                    </Button>
                    <Button type="primary" onClick={() => form.submit()} data-test={'createForecastSubmitButton'}>
                        <FormattedMessage {...messages.create} />
                    </Button>
                </div>
            )}
        </div>
    )
}

const styles = {
    button: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
    },
    closeEditing: {
        float: 'right',
        marginBottom: 'var(--s1)',
    },
    container: {
        display: 'flex',
    },
    cancelButton: {
        margin: '0 var(--s0) 0 0',
        color: '#fff',
        background: 'var(--grey)',
        borderColor: 'var(--grey)',
    },
    deleteButton: {
        marginTop: 'var(--s1)',
        marginLeft: 'auto',
        position: 'fixed',
        bottom: 'var(--s0)',
        right: 'var(--s0)',
    },
    buttonContainer: {
        display: 'flex',
        alignItems: 'right',
        justifyContent: 'right',
    },
    errorMessage: {
        fontSize: '14px',
        marginTop: '-25px',
        color: 'var(--red)',
        fontWeight: '300',
    },
    hide: {
        display: 'none',
    },
    show: {
        display: 'block',
    },
    aspectRose: {
        marginBottom: 'var(--s0)',
    },
    sudoLabel: {
        fontSize: '14px',
    },
    title: {
        fontWeight: 300,
    },
}
